/*
  Popup « Ajouté au panier » (snippets/cart-added-popup.liquid).

  S'ouvre à chaque ajout au panier réussi : écoute l'événement global
  `kt.cart.added` dispatché par Cart.add (assets/cart.js) — donc couvre la page
  produit, l'ajout rapide des cartes produit et les sliders cross-sell.
  L'ouverture automatique du panier latéral est neutralisée dans cart.js quand
  ce composant est présent (il n'est pas rendu sur la page panier).

  Après l'ouverture :
    - GET /cart.js → total, nombre d'articles, ligne ajoutée (barre livraison
      offerte + ligne « Total » + quantité réelle)
    - GET {produit}?variant=…&section_id=cart-added-popup → section AJAX
      sections/cart-added-popup.liquid rendue dans le contexte du produit
      ajouté : prix barré, quantité minimale, produits complémentaires
      (métachamp custom.cross_sell_main)

  Ajout multiple (kits : le kit puis fixation, câble, batterie,
  accessoires… en un seul /cart/add.js) : le 1er produit de la réponse reste
  le produit principal (stepper, prix barré, complémentaires) ; les suivants
  sont listés dessous (« Également ajoutés au panier »), quantité et prix
  recalés sur /cart.js comme le produit principal.

  Le stepper « Quantité » est optimiste : chaque clic met à jour l'affichage
  immédiatement (quantité, prix de ligne, total et barre estimés), puis la
  rafale de clics est regroupée en UN seul /cart/change.js via
  cart-container.change() (badge compteur et tiroir resynchronisés par le
  thème) ; la quantité minimale respecte la règle thème (quantity_rule /
  metafield) ET la règle Pareto (window.mdsParetoMinQuantity, exposée par
  assets/custom.js).

  Les boutons « + » des produits complémentaires portent la classe
  .cap-upsell-add : ils sont gérés par le handler délégué de assets/custom.js
  (même logique que .product-card-atc, quantité minimale Pareto comprise).
  Un ajout effectué pendant que le popup est ouvert ne le reconstruit pas :
  il met à jour la barre, le total et l'état du bouton, et l'annonce dans la
  région aria-live. Les erreurs d'ajout/modification (kt.cart.error, dispatché
  par cart.js) s'affichent dans le bandeau .cap-error du popup quand il est
  ouvert.
*/

(function () {
  'use strict'

  // Produit-service « véhicule adapté » (rue non accessible aux 19 t) :
  // supplément de livraison, exclu de la barre « livraison offerte ».
  const SPECIAL_VEHICLE_HANDLE = 'service-de-livraison-vehicule-adapte-rue-non-accessible-aux-19-t'

  function formatMoney (cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents)
    }
    return (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €'
  }

  function resizeImageUrl (src, width) {
    try {
      const url = new URL(src, window.location.origin)
      url.searchParams.set('width', String(width))
      return url.toString()
    } catch (err) {
      return src
    }
  }

  class CartAddedPopup extends HTMLElement {
    constructor () {
      super()

      this.modalEl = this.querySelector('.modal')
      this.contentEl = this.querySelector('.cap-content')
      this.upsellItemsEl = this.querySelector('[data-cap-upsell-items]')
      this.errorEl = this.querySelector('[data-cap-error]')
      this.announceEl = this.querySelector('[data-cap-announce]')
      this.extrasEl = this.querySelector('[data-cap-extras]')
      this.extrasListEl = this.querySelector('[data-cap-extras-list]')
      this.extrasTitleEl = this.querySelector('[data-cap-extras-title]')
      this.extras = []
      // Fenêtre de 3 lignes et demie de la liste « Également ajoutés »,
      // mesurée dès que la liste reçoit une mise en page (le modal est en
      // display:none jusqu'à l'ouverture), avant le premier affichage, puis à
      // chaque changement de largeur. On observe la 1re ligne et non la
      // liste, dont la hauteur change justement avec la mesure (boucle).
      this.extrasObserver = 'ResizeObserver' in window
        ? new ResizeObserver(() => this.sizeExtrasList())
        : null
      // Titres au singulier (snippet) : passés au pluriel pour un ajout multiple
      this.titleEls = Array.from(this.querySelectorAll('.cap-title-full, .cap-title-short'))
      this.titleEls.forEach(el => { el.dataset.singular = el.textContent })
      this.skeletonHTML = this.upsellItemsEl ? this.upsellItemsEl.innerHTML : ''
      this.threshold = parseInt(this.dataset.freeShippingThreshold, 10) || 0
      // Ids produit hors offre livraison offerte (carports), posés par le snippet
      this.excludedProductIds = new Set((this.dataset.freeShippingExcludedProducts || '')
        .split(',').map(id => id.trim()).filter(Boolean))
      this.sectionId = this.dataset.sectionId || 'cart-added-popup'
      this.sectionToken = 0
      this.cartToken = 0
      this.line = null
      this.pendingItem = null
      this.qtyBusy = false
      this.qtyTimer = null
      this.qtyTarget = null
      this.lastCart = null

      window.addEventListener('kt.cart.added', event => {
        this.onCartAdded(event.detail).catch(err => console.error('MDS popup panier :', err))
      })
      window.addEventListener('kt.cart.changed', event => {
        if (this.isOpen && event.detail && Array.isArray(event.detail.items)) {
          // Cet état est plus frais que toute relecture /cart.js encore en
          // vol (refreshCart) : on périme ces dernières avant d'appliquer.
          this.cartToken++
          this.syncFromCart(event.detail)
        }
      })
      window.addEventListener('kt.cart.error', event => this.onCartError(event.detail))

      // La fenêtre de 3 produits complémentaires visibles dépend du
      // breakpoint : on la remesure quand la fenêtre change de taille.
      this.resizeTimer = null
      window.addEventListener('resize', () => {
        if (!this.isOpen) return
        clearTimeout(this.resizeTimer)
        this.resizeTimer = setTimeout(() => this.sizeUpsellList(), 150)
      })

      if (this.modalEl) {
        // Pendant la transition de fermeture, #cart-added-popup perd .show :
        // on garde le backdrop au-dessus du header via une classe sur <html>.
        this.modalEl.addEventListener('hide.bs.modal', () => {
          document.documentElement.classList.add('cap-modal-transition')
        })
        this.modalEl.addEventListener('hidden.bs.modal', () => {
          document.documentElement.classList.remove('cap-modal-transition')
          if (this.pendingItem) {
            const pending = this.pendingItem
            this.pendingItem = null
            this.showFor(pending.item, pending.extras)
          }
        })
        this.modalEl.addEventListener('shown.bs.modal', () => {
          this.pendingItem = null
          // Si la section AJAX est arrivée alors que le modal n'était pas
          // encore mesurable, la fenêtre de 3 produits n'a pas pu être
          // calculée : on la remesure maintenant.
          this.sizeUpsellList()
        })
      }

      const minusBtn = this.querySelector('[data-cap-qty-minus]')
      const plusBtn = this.querySelector('[data-cap-qty-plus]')
      if (minusBtn) minusBtn.addEventListener('click', () => this.changeQty(-1))
      if (plusBtn) plusBtn.addEventListener('click', () => this.changeQty(1))

      this.initDragScroll()
    }

    // Tablette : la liste horizontale des complémentaires se tire aussi à la
    // souris, comme au doigt. Le tactile garde son défilement natif (gestes
    // pointerType « mouse » uniquement). Le glissement ne démarre qu'au-delà
    // de 5px pour préserver le clic sur les liens et boutons « + » ; pendant
    // le glissement le snap CSS est suspendu (classe cap-upsell-dragging),
    // sinon chaque scrollLeft posé serait recollé à une carte.
    initDragScroll () {
      const list = this.upsellItemsEl
      if (!list) return

      const state = { pointerId: null, startX: 0, startScroll: 0, dragging: false }

      list.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return
        if (list.scrollWidth <= list.clientWidth + 1) return
        state.pointerId = event.pointerId
        state.startX = event.clientX
        state.startScroll = list.scrollLeft
        state.dragging = false
        // empêche la sélection de texte au glissement ; le click, lui,
        // est toujours dispatché malgré le preventDefault sur pointerdown
        event.preventDefault()
      })

      list.addEventListener('pointermove', event => {
        if (state.pointerId !== event.pointerId) return
        if (!(event.buttons & 1)) { state.pointerId = null; return }
        if (!state.dragging) {
          if (Math.abs(event.clientX - state.startX) < 5) return
          state.dragging = true
          // rebase : le mouvement d'armement (5px) ne fait pas sauter la liste
          state.startX = event.clientX
          state.startScroll = list.scrollLeft
          list.classList.add('cap-upsell-dragging')
          try { list.setPointerCapture(event.pointerId) } catch (err) { /* pointeur déjà relâché */ }
        }
        list.scrollLeft = state.startScroll - (event.clientX - state.startX)
      })

      const endDrag = event => {
        if (state.pointerId !== event.pointerId) return
        state.pointerId = null
        if (!state.dragging) return
        // le snap se réapplique : la liste se recolle à la carte la plus proche
        list.classList.remove('cap-upsell-dragging')
        // drapeau consommé par le listener click (capture) ci-dessous ; le
        // timeout couvre le cas où aucun click ne suit le relâchement
        setTimeout(() => { state.dragging = false }, 0)
      }
      list.addEventListener('pointerup', endDrag)
      list.addEventListener('pointercancel', endDrag)

      // un vrai glissement ne doit pas activer le lien/bouton sous le curseur
      list.addEventListener('click', event => {
        if (!state.dragging) return
        state.dragging = false
        event.preventDefault()
        event.stopPropagation()
      }, true)

      // ni démarrer un drag-and-drop natif de lien ou d'image
      list.addEventListener('dragstart', event => {
        if (state.pointerId !== null || state.dragging) event.preventDefault()
      })
    }

    get isOpen () {
      return this.modalEl && this.modalEl.classList.contains('show')
    }

    setAll (selector, text) {
      this.querySelectorAll(selector).forEach(elem => { elem.textContent = text })
    }

    announce (message) {
      if (this.announceEl) this.announceEl.textContent = message
    }

    showError (message) {
      if (!this.errorEl) return
      this.errorEl.textContent = message
      this.errorEl.hidden = false
    }

    hideError () {
      if (this.errorEl) this.errorEl.hidden = true
    }

    onCartError (detail) {
      if (!this.isOpen) return
      let message = "L'ajout au panier a échoué."
      if (detail && typeof detail.description === 'string' && detail.description) {
        message = detail.description
      } else if (detail && typeof detail.message === 'string' && detail.message) {
        message = detail.message
      }
      this.showError(message)

      // Un /cart/change.js refusé pendant une rafale du stepper laisse
      // l'affichage optimiste en avance sur le panier réel : on recale.
      if (this.qtyTarget != null) {
        clearTimeout(this.qtyTimer)
        this.qtyTarget = null
        this.refreshCart()
      }
    }

    async onCartAdded (detail) {
      if (!this.modalEl || !window.bootstrap || !detail || typeof detail !== 'object') return

      // Réponse /cart/add.js : objet ligne pour un ajout simple,
      // { items: [...] } pour un ajout multiple (kits : le kit en tête).
      const items = (Array.isArray(detail.items) ? detail.items : [detail])
        .filter(it => it && it.id)
      const item = items[0]
      if (!item) return

      if (this.isOpen) {
        // Ajout déclenché depuis le popup lui-même (bouton « + ») : on met à
        // jour totaux/barre et l'état du bouton sans reconstruire le contenu.
        this.hideError()
        items.forEach(it => this.markAdded(it.variant_id || it.id))
        this.refreshCart()
        return
      }

      this.showFor(item, items.slice(1))
    }

    showFor (item, extras = []) {
      const modal = window.bootstrap.Modal.getOrCreateInstance(this.modalEl)

      // Modal en cours de FERMETURE (transition avec _isShown retombé) :
      // Bootstrap ignorerait show() — on rejoue l'ouverture sur
      // hidden.bs.modal. En cours d'OUVERTURE (deux ajouts rapprochés depuis
      // une collection), on continue : le contenu bascule sur le nouveau
      // produit et le show() redondant est sans effet.
      if (modal._isTransitioning && !modal._isShown) {
        this.pendingItem = { item, extras }
        return
      }

      // Nouveau produit affiché : la rafale du stepper du précédent est caduque
      clearTimeout(this.qtyTimer)
      this.qtyTarget = null
      this.lastCart = null

      this.line = {
        key: item.key || null,
        variantId: item.variant_id || item.id,
        productId: item.product_id != null ? String(item.product_id) : null,
        quantity: item.quantity || 1,
        minQty: 1,
        compareAtUnit: 0,
        unitPrice: item.final_price != null ? item.final_price : (item.price != null ? item.price : null)
      }

      // Produit ajouté (données de la réponse /cart/add.js)
      this.setAll('[data-cap-title]', item.product_title || item.title || '')

      this.querySelectorAll('[data-cap-url]').forEach(link => {
        if (item.url) link.setAttribute('href', item.url)
      })

      const vendorEl = this.querySelector('[data-cap-vendor]')
      if (vendorEl) {
        const vendor = (item.vendor || '').toUpperCase() === 'EURION ENERGY' ? '' : item.vendor
        vendorEl.textContent = vendor || ''
        vendorEl.hidden = !vendor
      }

      const imgEl = this.querySelector('[data-cap-image]')
      if (imgEl) {
        if (item.image) {
          imgEl.src = resizeImageUrl(item.image, 152)
          imgEl.alt = ''
          imgEl.parentElement.hidden = false
        } else {
          imgEl.removeAttribute('src')
          imgEl.parentElement.hidden = true
        }
      }

      this.setAll('[data-cap-qty]', String(this.line.quantity))
      this.setAll('[data-cap-price]', formatMoney(item.final_line_price != null ? item.final_line_price : item.line_price))

      const compareEl = this.querySelector('[data-cap-compare]')
      if (compareEl) compareEl.hidden = true

      this.renderExtras(item, extras)

      this.hideError()
      this.announce('')
      this.updateQtyButtons()

      // Upsell : état de chargement, panneau visible par défaut ; la hauteur
      // mesurée pour le produit précédent ne s'applique pas aux squelettes
      if (this.upsellItemsEl) {
        this.upsellItemsEl.innerHTML = this.skeletonHTML
        this.upsellItemsEl.style.removeProperty('--cap-upsell-3-rows')
        this.upsellItemsEl.classList.remove('cap-upsell-draggable')
      }
      if (this.contentEl) this.contentEl.classList.remove('cap-no-upsell')

      modal.show()

      this.refreshCart()
      this.loadSection(item)
    }

    async loadSection (item) {
      const token = ++this.sectionToken

      try {
        const url = new URL(item.url, window.location.origin)
        url.search = ''
        url.searchParams.set('variant', String(item.variant_id || item.id))
        url.searchParams.set('section_id', this.sectionId)

        // Même filet que refreshCart : un échec ponctuel ne doit pas priver le
        // popup de son panneau de produits complémentaires. Le rendu de section
        // (?section_id=…) renvoie des 503 passagers sur cette boutique, parfois
        // au-delà d'une seconde : on insiste donc plus longtemps que refreshCart,
        // le squelette de chargement reste affiché entre les essais.
        let html = null
        for (const delay of [0, 600, 1500, 3000]) {
          if (delay) await new Promise(resolve => setTimeout(resolve, delay))
          if (token !== this.sectionToken) return
          try {
            const response = await fetch(url.toString())
            if (!response.ok) continue
            html = await response.text()
            break
          } catch (err) {
            // réseau : nouvel essai
          }
        }
        if (token !== this.sectionToken) return
        if (html === null) throw new Error('section indisponible')

        const doc = new DOMParser().parseFromString(html, 'text/html')

        // Prix barré + quantité minimale de la variante ajoutée
        const meta = doc.querySelector('[data-cap-meta]')
        if (meta && this.line) {
          const compareAt = parseInt(meta.dataset.compareAt, 10) || 0
          const unitPrice = parseInt(meta.dataset.price, 10) || 0
          if (compareAt > unitPrice) {
            this.line.compareAtUnit = compareAt
            this.renderCompare()
          }

          let minQty = Math.max(1, parseInt(meta.dataset.minQuantity, 10) || 1)
          if (typeof window.mdsParetoMinQuantity === 'function') {
            minQty = Math.max(minQty, window.mdsParetoMinQuantity(meta.dataset.productId, meta.dataset.collectionIds))
          }
          this.line.minQty = minQty
          this.updateQtyButtons()
        }

        // Produits complémentaires
        const items = doc.querySelector('[data-cap-upsell-items]')
        if (items && items.children.length > 0 && this.upsellItemsEl) {
          this.upsellItemsEl.innerHTML = items.innerHTML
          this.sizeUpsellList()
        } else if (this.contentEl) {
          this.contentEl.classList.add('cap-no-upsell')
        }
      } catch (err) {
        if (token === this.sectionToken && this.contentEl) {
          this.contentEl.classList.add('cap-no-upsell')
        }
      }
    }

    // Fenêtre de 3 produits complémentaires visibles : mesure la hauteur des
    // 3 premières lignes et l'expose en variable CSS, utilisée comme
    // max-height par les listes verticales (mobile et desktop). La tablette
    // défile horizontalement et ignore la variable (max-height: none). Avec
    // 3 produits ou moins la liste tient en entier : pas de limite.
    sizeUpsellList () {
      if (!this.upsellItemsEl) return
      this.upsellItemsEl.style.removeProperty('--cap-upsell-3-rows')
      // curseur de prise (tablette) seulement quand la liste déborde vraiment
      this.upsellItemsEl.classList.toggle(
        'cap-upsell-draggable',
        this.upsellItemsEl.scrollWidth > this.upsellItemsEl.clientWidth + 1
      )
      const items = this.upsellItemsEl.querySelectorAll('.cap-upsell-item')
      if (items.length <= 3) return
      const height = items[2].getBoundingClientRect().bottom -
        items[0].getBoundingClientRect().top
      if (height > 0) {
        this.upsellItemsEl.style.setProperty('--cap-upsell-3-rows', Math.ceil(height) + 'px')
      }
    }

    renderCompare () {
      const compareEl = this.querySelector('[data-cap-compare]')
      if (!compareEl || !this.line) return
      if (this.line.compareAtUnit > 0) {
        compareEl.textContent = formatMoney(this.line.compareAtUnit * this.line.quantity)
        compareEl.hidden = false
      } else {
        compareEl.hidden = true
      }
    }

    // Autres produits du même ajout (kits) : une ligne compacte chacun —
    // vignette, titre, variante et quantité, prix de ligne. Le produit
    // principal n'y figure pas, et une variante envoyée deux fois (fusionnée
    // en une seule ligne par Shopify) n'apparaît qu'une fois. Liste vide :
    // bloc masqué et titre du popup au singulier.
    renderExtras (main, extras) {
      this.extras = []
      const seen = new Set([main.key || String(main.variant_id || main.id)])
      extras.forEach(it => {
        const id = it.key || String(it.variant_id || it.id)
        if (seen.has(id)) return
        seen.add(id)
        this.extras.push({ key: it.key || null, variantId: it.variant_id || it.id, els: null, item: it })
      })

      const plural = this.extras.length > 0
      this.titleEls.forEach(el => {
        el.textContent = !plural ? el.dataset.singular
          : el.classList.contains('cap-title-full') ? 'Produits ajoutés à votre panier !' : 'Ajoutés au panier !'
      })

      if (!this.extrasEl || !this.extrasListEl) return
      if (this.extrasObserver) this.extrasObserver.disconnect()
      this.extrasListEl.textContent = ''
      this.extrasListEl.scrollTop = 0
      this.extrasEl.hidden = !plural
      this.sizeExtrasList()
      if (!plural) return

      if (this.extrasTitleEl) {
        this.extrasTitleEl.textContent = this.extras.length > 1
          ? 'Également ajoutés au panier (' + this.extras.length + ')'
          : 'Également ajouté au panier'
      }

      this.extras.forEach(extra => {
        const it = extra.item
        const row = document.createElement('li')
        row.className = 'cap-extra'

        const media = document.createElement('span')
        media.className = 'cap-extra-img'
        if (it.image) {
          const img = document.createElement('img')
          img.src = resizeImageUrl(it.image, 80)
          img.alt = ''
          img.width = 40
          img.height = 40
          img.decoding = 'async'
          media.appendChild(img)
        }

        const info = document.createElement('span')
        info.className = 'cap-extra-info'
        const title = document.createElement(it.url ? 'a' : 'span')
        title.className = 'cap-extra-title'
        if (it.url) title.href = it.url
        title.textContent = it.product_title || it.title || ''
        const meta = document.createElement('span')
        meta.className = 'cap-extra-meta'
        info.append(title, meta)

        const price = document.createElement('span')
        price.className = 'cap-extra-price'
        const amount = document.createElement('strong')
        const compare = document.createElement('s')
        price.append(amount, compare)

        row.append(media, info, price)
        this.extrasListEl.appendChild(row)
        extra.els = { meta, amount, compare }
        this.renderExtraLine(extra, it)
      })

      const firstRow = this.extrasListEl.firstElementChild
      if (this.extrasObserver && firstRow && this.extras.length > 3) {
        this.extrasObserver.observe(firstRow)
      }
      this.sizeExtrasList()
    }

    // Plus de 3 lignes : hauteur limitée au milieu de la 4e, la ligne coupée
    // signale le défilement. Mesure en offsetTop/offsetHeight (repère de la
    // liste, comme max-height — insensible au zoom desktop de la page) ;
    // sans mise en page (modal fermé), on attend le ResizeObserver.
    sizeExtrasList () {
      const list = this.extrasListEl
      if (!list) return
      const fourth = list.children[3]
      if (!fourth || !fourth.offsetHeight) {
        if (!fourth) list.style.removeProperty('--cap-extras-max')
        return
      }
      const borders = list.offsetHeight - list.clientHeight
      const height = fourth.offsetTop + Math.round(fourth.offsetHeight / 2) + borders
      list.style.setProperty('--cap-extras-max', height + 'px')
    }

    renderExtraLine (extra, line) {
      if (!extra.els) return
      const variant = line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : ''
      extra.els.meta.textContent = (variant ? variant + ' · ' : '') + 'Qté : ' + line.quantity

      const final = line.final_line_price != null ? line.final_line_price : line.line_price
      extra.els.amount.textContent = formatMoney(final)
      // Remise Shopify sur la ligne (ex. batterie supplémentaire en quantité)
      const original = line.original_line_price
      extra.els.compare.hidden = !(original > final)
      extra.els.compare.textContent = original > final ? formatMoney(original) : ''
    }

    // Quantités et prix des lignes « Également ajoutés » d'après le panier lu
    // (remises calculées, quantité déjà présente avant l'ajout comprise).
    syncExtras (cart) {
      if (!this.extras.length || !Array.isArray(cart.items)) return
      this.extras.forEach(extra => {
        const line = cart.items.find(it => (extra.key && it.key === extra.key) ||
          (!extra.key && it.variant_id === extra.variantId))
        if (!line) return
        extra.key = line.key
        this.renderExtraLine(extra, line)
      })
    }

    // La lecture /cart.js qui suit immédiatement un ajout échoue parfois
    // (réponse en retard de l'écriture, non-JSON ou hoquet réseau — constaté
    // sur l'ajout rapide depuis les pages collection) : sans nouvel essai le
    // popup resterait figé sur « Total · 0 article(s) » et la barre de
    // livraison vide. On retente donc brièvement ; si le panier lu ne
    // contient pas encore la ligne ajoutée, il est en retard et on retente
    // aussi (le dernier état lu est appliqué en dernier recours).
    async refreshCart () {
      const token = ++this.cartToken
      const cartUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_url) || '/cart'
      let cart = null

      for (const delay of [0, 400, 1100]) {
        if (delay) await new Promise(resolve => setTimeout(resolve, delay))
        if (token !== this.cartToken) return

        try {
          const response = await fetch(cartUrl + '.js', {
            cache: 'no-store',
            headers: { Accept: 'application/json' }
          })
          if (!response.ok) continue
          cart = await response.json()
          if (this.hasLine(cart)) break
        } catch (err) {
          // réseau ou réponse non-JSON : nouvel essai
        }
      }

      if (token !== this.cartToken || !cart) return
      this.syncFromCart(cart)
    }

    hasLine (cart) {
      if (!this.line) return true
      return Boolean(this.findLine(cart))
    }

    findLine (cart) {
      if (!this.line || !Array.isArray(cart.items)) return null
      return cart.items.find(it => (this.line.key && it.key === this.line.key) ||
        (!this.line.key && it.variant_id === this.line.variantId)) || null
    }

    // Met à jour barre de livraison, total, compteur d'articles et la ligne du
    // produit ajouté à partir d'un objet panier complet (/cart.js ou réponse
    // de /cart/change.js).
    syncFromCart (cart) {
      this.lastCart = cart

      const line = this.findLine(cart)
      if (line) {
        this.line.key = line.key
        this.line.unitPrice = line.final_price != null ? line.final_price : line.price
      }

      // Indépendantes du stepper : toujours à jour, rafale en cours ou non
      this.syncExtras(cart)

      // Rafale de clics du stepper en cours : cet état serveur est déjà
      // dépassé, l'affichage optimiste prime — flushQty rejouera le dernier
      // panier reçu (lastCart) une fois la rafale réellement envoyée.
      if (this.qtyTarget != null && (!line || line.quantity !== this.qtyTarget)) {
        return
      }

      if (typeof cart.total_price === 'number') this.updateShipping(this.shippingTotal(cart))
      if (typeof cart.item_count === 'number') this.setAll('[data-cap-count]', String(cart.item_count))
      if (typeof cart.total_price === 'number') this.setAll('[data-cap-total]', formatMoney(this.displayTotal(cart)))
      this.renderSpecialVehicle(cart)

      if (line) {
        this.line.quantity = line.quantity
        this.setAll('[data-cap-qty]', String(line.quantity))
        this.setAll('[data-cap-price]', formatMoney(line.final_line_price != null ? line.final_line_price : line.line_price))
        this.renderCompare()
      }

      this.updateQtyButtons()
    }

    // Prix de la ligne du produit-service « véhicule adapté » dans le panier
    // (0 s'il n'y est pas).
    specialVehicleTotal (cart) {
      let total = 0
      if (Array.isArray(cart.items)) {
        cart.items.forEach(item => {
          if (item.handle === SPECIAL_VEHICLE_HANDLE) {
            total += item.final_line_price != null ? item.final_line_price : item.line_price
          }
        })
      }
      return total
    }

    // Total affiché (data-cap-total) : panier hors produit-service « véhicule
    // adapté », qui a sa propre ligne « Service de livraison spécial ».
    displayTotal (cart) {
      return Math.max(0, cart.total_price - this.specialVehicleTotal(cart))
    }

    isShippingExcluded (productId) {
      return productId != null && this.excludedProductIds.has(String(productId))
    }

    // Prix des lignes hors offre livraison offerte (carports).
    excludedTotal (cart) {
      let total = 0
      if (Array.isArray(cart.items)) {
        cart.items.forEach(item => {
          if (this.isShippingExcluded(item.product_id)) {
            total += item.final_line_price != null ? item.final_line_price : item.line_price
          }
        })
      }
      return total
    }

    // Total retenu pour la barre « livraison offerte » : ni le service
    // « véhicule adapté » (un supplément de livraison ne doit pas rapprocher
    // de la livraison offerte) ni les carports (hors offre).
    shippingTotal (cart) {
      return Math.max(0, this.displayTotal(cart) - this.excludedTotal(cart))
    }

    // Affiche/masque la ligne « Service de livraison spécial » du bloc total
    // selon la présence du produit-service dans le panier.
    renderSpecialVehicle (cart) {
      const wrap = this.querySelector('[data-cap-special-vehicle]')
      if (!wrap) return
      const total = this.specialVehicleTotal(cart)
      wrap.hidden = total <= 0
      if (total > 0) this.setAll('[data-cap-special-vehicle-amount]', formatMoney(total))
    }

    updateShipping (totalPrice) {
      if (!this.threshold) return

      const pct = Math.min(100, (totalPrice / this.threshold) * 100)
      const remaining = Math.max(0, this.threshold - totalPrice)
      const reached = totalPrice >= this.threshold

      const bar = this.querySelector('[data-cap-bar]')
      if (bar) bar.style.width = pct.toFixed(1) + '%'

      const progress = this.querySelector('.cap-shipping-bar')
      if (progress) progress.setAttribute('aria-valuenow', pct.toFixed(0))

      const remainingWrap = this.querySelector('[data-cap-shipping-remaining]')
      const reachedWrap = this.querySelector('[data-cap-shipping-reached]')
      if (remainingWrap) remainingWrap.hidden = reached
      if (reachedWrap) reachedWrap.hidden = !reached

      this.setAll('[data-cap-remaining]', formatMoney(remaining))
    }

    updateQtyButtons () {
      const minusBtn = this.querySelector('[data-cap-qty-minus]')
      const plusBtn = this.querySelector('[data-cap-qty-plus]')
      const min = this.line ? Math.max(1, this.line.minQty || 1) : 1

      // Pas de verrou pendant la requête : le stepper est optimiste, les
      // clics s'enchaînent et la rafale est regroupée par changeQty.
      if (minusBtn) minusBtn.disabled = !this.line || this.line.quantity <= min
      if (plusBtn) plusBtn.disabled = !this.line
    }

    // Chaque clic met à jour l'affichage immédiatement (quantité, prix de
    // ligne, total et barre estimés) ; l'envoi est regroupé — un seul
    // /cart/change.js, 350 ms après le dernier clic de la rafale. Attendre
    // un aller-retour serveur par clic (avec re-rendu de sections) rendait
    // le stepper poussif.
    changeQty (delta) {
      if (!this.line) return

      const min = Math.max(1, this.line.minQty || 1)
      const target = Math.max(min, this.line.quantity + delta)
      if (target === this.line.quantity) return

      this.hideError()
      this.line.quantity = target
      this.qtyTarget = target
      this.setAll('[data-cap-qty]', String(target))
      if (this.line.unitPrice != null) {
        this.setAll('[data-cap-price]', formatMoney(this.line.unitPrice * target))
      }
      this.renderCompare()
      this.renderOptimisticTotals(target)
      this.updateQtyButtons()

      clearTimeout(this.qtyTimer)
      this.qtyTimer = setTimeout(() => this.flushQty(), 350)
    }

    // Estime total, compteur d'articles et barre de livraison sans attendre
    // le serveur (prix unitaire × écart de quantité appliqué au dernier
    // panier connu) ; la réponse /cart/change.js recale ensuite les valeurs
    // exactes (remises, plafond de stock…).
    renderOptimisticTotals (target) {
      const cart = this.lastCart
      if (!cart || !this.line || this.line.unitPrice == null) return
      if (typeof cart.total_price !== 'number') return

      const line = this.findLine(cart)
      const diff = target - (line ? line.quantity : 0)
      const delta = this.line.unitPrice * diff
      // Un carport ajouté ne fait pas avancer la barre, seulement le total
      const shippingDelta = this.isShippingExcluded(this.line.productId) ? 0 : delta
      this.updateShipping(Math.max(0, this.shippingTotal(cart) + shippingDelta))
      this.setAll('[data-cap-total]', formatMoney(Math.max(0, this.displayTotal(cart) + delta)))
      if (typeof cart.item_count === 'number') {
        this.setAll('[data-cap-count]', String(Math.max(0, cart.item_count + diff)))
      }
    }

    // Envoie la dernière quantité visée par la rafale. Une seule requête en
    // vol à la fois ; si de nouveaux clics arrivent entre-temps, la fin de
    // celle-ci en déclenche une autre avec la nouvelle cible.
    async flushQty () {
      if (this.qtyBusy || this.qtyTarget == null || !this.line) return

      const target = this.qtyTarget
      const key = this.line.key || String(this.line.variantId)
      this.qtyBusy = true

      try {
        const cartEl = document.querySelector('cart-container')

        if (cartEl && typeof cartEl.change === 'function') {
          // Dispatch kt.cart.changed (réponse = panier complet) → syncFromCart
          // via le listener ; badge compteur et tiroir resynchronisés.
          await cartEl.change(key, target)
        } else {
          const changeUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_change_url) || '/cart/change'
          const response = await fetch(changeUrl + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: target })
          })
          const data = await response.json()
          if (response.ok) {
            // Réponse plus fraîche que toute relecture /cart.js en vol
            this.cartToken++
            this.syncFromCart(data)
          } else {
            this.onCartError(data)
          }
        }

        if (this.line) this.announce('Quantité mise à jour : ' + this.line.quantity)
      } catch (err) {
        console.error('MDS popup panier :', err)
        this.showError("La quantité n'a pas pu être mise à jour. Veuillez réessayer.")
      } finally {
        this.qtyBusy = false
        if (this.qtyTarget != null && this.qtyTarget !== target) {
          // De nouveaux clics pendant la requête : on envoie la nouvelle cible
          this.flushQty()
        } else if (this.qtyTarget != null) {
          this.qtyTarget = null
          // Recale l'affichage sur le dernier panier reçu (quantité plafonnée
          // par le stock, requête échouée, relecture arrivée pendant la
          // rafale…).
          if (this.lastCart) this.syncFromCart(this.lastCart)
        }
      }
    }

    markAdded (variantId) {
      if (!variantId) return
      this.querySelectorAll('.cap-upsell-add[data-variant-id="' + variantId + '"]').forEach(btn => {
        btn.classList.add('is-added')
        const title = btn.dataset.productTitle || ''
        if (title) btn.setAttribute('aria-label', 'Ajouté au panier : ' + title)
        this.announce(title ? '« ' + title + ' » a été ajouté au panier.' : 'Produit ajouté au panier.')
      })
    }
  }

  if (!customElements.get('cart-added-popup')) {
    customElements.define('cart-added-popup', CartAddedPopup)
  }
})()
